import * as React from 'react';

import * as ramda from 'ramda';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import MultiGrid from 'react-virtualized/dist/es/MultiGrid';

import type {ComparedActivityReportSummary, ReportRowData} from 'nutshell-core/types';

import {ReportEmptyView} from '../../report-empty-view';
import type {SortParams} from '../../report-types';

import {USER_SORT_STRING} from '../constants';

import {ActivityTableHeaderCell} from './activity-table-header-cell';
import {ActivityTableNameCell} from './activity-table-name-cell';
import {ActivityTableQuotaCell} from './activity-table-quota-cell';

import styles from './activity-report-summary-table.module.css';

const TABLE_ROW_HEADER_HEIGHT = 35;

const TABLE_ROW_HEIGHT_OLD = 60;
const TABLE_ROW_HEIGHT_FF = 55;
const TABLE_ROW_HEIGHT = NutClientConfig.hasActivityReportExpansionFF
    ? TABLE_ROW_HEIGHT_FF
    : TABLE_ROW_HEIGHT_OLD;

const TABLE_ROW_DOUBLE_HEIGHT_OLD = 95;
const TABLE_ROW_DOUBLE_HEIGHT_FF = 85;
const TABLE_ROW_DOUBLE_HEIGHT = NutClientConfig.hasActivityReportExpansionFF
    ? TABLE_ROW_DOUBLE_HEIGHT_FF
    : TABLE_ROW_DOUBLE_HEIGHT_OLD;

const TABLE_COLUMN_USER_WIDTH = 250;

const TABLE_COLUMN_WIDTH_NO_COMPARISON_OLD = 300;
const TABLE_COLUMN_WIDTH_NO_COMPARISON_FF = 150;
const TABLE_COLUMN_WIDTH_NO_COMPARISON = NutClientConfig.hasActivityReportExpansionFF
    ? TABLE_COLUMN_WIDTH_NO_COMPARISON_FF
    : TABLE_COLUMN_WIDTH_NO_COMPARISON_OLD;

const TABLE_COLUMN_WIDTH_COMPARISON_OLD = 300;
const TABLE_COLUMN_WIDTH_COMPARISON_FF = 190;
const TABLE_COLUMN_WIDTH_COMPARISON = NutClientConfig.hasActivityReportExpansionFF
    ? TABLE_COLUMN_WIDTH_COMPARISON_FF
    : TABLE_COLUMN_WIDTH_COMPARISON_OLD;

type Props = {
    isLoading: boolean;
    hasError: boolean;
    data: ComparedActivityReportSummary<Array<ReportRowData>> | null | undefined;
    totals: ComparedActivityReportSummary<ReportRowData> | null | undefined;
    stickyHeaderTop: number | null | undefined;
    onErrorRetry: () => any;
    onColumnSort: (columnTitle: string) => void;
    activeSort: SortParams | null | undefined;
};

type State = {
    scrollLeft: number;
    scrollToColumn: number | null | undefined;
};

/**
 * Helper method to calculate the number of grids for our MultiGrid view (either # of columns or # of rows) based on the
 * fixed size of each grid and the size of container. Works for both height and width.
 *
 * The purpose of this is to fill in extra empty columns/rows so that we fill the container
 * and don't leave awkward whitespace to the right or bottom.
 *
 * @param {number} containerSize The width or height of the container, probably from the AutoSizer component.
 * @param {number} gridSize The width or height of each individual grid, based on whether you're calculating rows or columns.
 * @param {number} rawCount The # of rows/columns to show if our container is large enough to show them all.
 * @param {function} roundingFunction Math.ceil or Math.floor
 * @returns {number} The number of grids to render.
 */
function calculateGridCount(
    containerSize: number,
    gridSize: number,
    rawCount: number,
    roundingFunction: (...args: any) => any
) {
    const gridsInView = roundingFunction(containerSize / gridSize);

    return Math.max(gridsInView, rawCount);
}

/*
 * Component representing the Summarys Table.
 */
export class ActivityReportSummaryTable extends React.Component<Props, State> {
    mutligrid: any;
    columnCount = 4;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            scrollLeft: 0,
            scrollToColumn: null,
        };
    }

    render() {
        return (
            <div className={styles.container}>
                {this.props.isLoading || this.props.hasError || this.isEmpty() ? (
                    <ReportEmptyView
                        isLoading={this.props.isLoading}
                        onErrorRetry={this.props.onErrorRetry}
                        hasError={this.props.hasError}
                    />
                ) : undefined}
                {this.props.data ? this.renderSummaryTable() : undefined}
            </div>
        );
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.data &&
            this.props.data &&
            nextProps.data.compareTo !== this.props.data.compareTo
        ) {
            this.mutligrid.recomputeGridSize();
        }

        if (
            nextProps.data &&
            nextProps.data.currentPeriod[0] &&
            this.props.data &&
            this.props.data.currentPeriod[0] &&
            nextProps.data.currentPeriod[0].segments &&
            this.props.data.currentPeriod[0].segments &&
            nextProps.data.currentPeriod[0].segments.length !==
                this.props.data.currentPeriod[0].segments.length
        ) {
            this.setState({scrollToColumn: 1});
        } else if (this.state.scrollToColumn) {
            this.setState({scrollToColumn: null});
        }
    }

    isEmpty() {
        if (!this.props.data) {
            return false;
        }

        return !this.props.data.currentPeriod.length;
    }

    renderSummaryTable() {
        if (!this.props.data || !this.props.totals) {
            return <div className={styles['grid-container']} />;
        }

        const {currentPeriod, compareTo} = this.props.data;
        const rowCount = currentPeriod.length;
        const rowCountWithTotal = rowCount + 1;
        const columnHeaders = getColumnHeaders(this.props.totals.currentPeriod);

        const rowHeight = !compareTo ? TABLE_ROW_HEIGHT : TABLE_ROW_DOUBLE_HEIGHT;
        const columnWidth = !compareTo
            ? TABLE_COLUMN_WIDTH_NO_COMPARISON
            : TABLE_COLUMN_WIDTH_COMPARISON;

        // TODO: add a minHeight, similar to the lead list
        const containerHeight = rowHeight * rowCountWithTotal + 50; // 50 extra is for title bar and bottom scroll bar

        return (
            <div className={styles['grid-container']}>
                <div style={{height: containerHeight}} className={styles['main-grid-container']}>
                    <AutoSizer>
                        {({height, width}) => {
                            const adjustedRowCount = calculateGridCount(
                                height,
                                rowHeight,
                                rowCountWithTotal,
                                Math.floor
                            );
                            this.columnCount = calculateGridCount(
                                width,
                                columnWidth,
                                columnHeaders.length + 1,
                                Math.ceil
                            );
                            const bottomRightGridStyle = {
                                overflowX:
                                    this.columnCount > columnHeaders.length + 1 ? 'hidden' : 'auto',
                                overflowY: adjustedRowCount > rowCountWithTotal ? 'hidden' : 'auto',
                            } as const;
                            // Account for small non-cell area above scrollbar in header
                            const topRightGridStyle = {
                                // @ts-expect-error
                                backgroundColor: styles['header-bg-color'],
                            } as const;

                            return (
                                <MultiGrid
                                    ref={(c) => {
                                        this.mutligrid = c;
                                    }}
                                    unusedCellData={this.props.data}
                                    fixedColumnCount={1}
                                    fixedRowCount={1}
                                    enableFixedColumnScroll={true}
                                    height={height}
                                    width={width}
                                    // @ts-expect-error
                                    scrollToColumn={this.state.scrollToColumn}
                                    onScroll={this.handleScroll}
                                    columnCount={this.columnCount}
                                    columnWidth={this.getTableColumnWidth}
                                    rowHeight={this.getRowHeight}
                                    rowCount={adjustedRowCount + 1}
                                    cellRenderer={this.cellRenderer}
                                    styleTopRightGrid={topRightGridStyle}
                                    styleBottomRightGrid={bottomRightGridStyle}
                                />
                            );
                        }}
                    </AutoSizer>
                </div>
                {/* If we need a sticky header, make our own to put on top of the table.  Too hard to hack react-virtualized */}
                {this.props.stickyHeaderTop && this.props.totals ? (
                    <div
                        style={{top: this.props.stickyHeaderTop}}
                        className={styles['static-header-row']}
                    >
                        {this.createHeaderCell(0, this.props.totals, '0', {
                            width: TABLE_COLUMN_USER_WIDTH,
                            zIndex: 1,
                        })}
                        <div
                            style={{
                                left: TABLE_COLUMN_USER_WIDTH - this.state.scrollLeft,
                            }}
                            className={styles['static-header-activities']}
                        >
                            {ramda.times((colIndex) => {
                                if (this.props.totals) {
                                    // Add 1 to skip first static `Users` header we already added
                                    return this.createHeaderCell(
                                        colIndex + 1,
                                        this.props.totals,
                                        String(colIndex),
                                        {
                                            width: columnWidth,
                                        }
                                    );
                                }

                                return <div key={colIndex} />;
                            }, this.columnCount)}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    handleScroll = (scroll: {scrollLeft: number}) => {
        this.setState({scrollLeft: scroll.scrollLeft});
    };

    getRowHeight = (row: {index: number}) => {
        if (row.index === 0) return TABLE_ROW_HEADER_HEIGHT;

        return !this.props.data || !this.props.data.compareTo
            ? TABLE_ROW_HEIGHT
            : TABLE_ROW_DOUBLE_HEIGHT;
    };

    getTableColumnWidth = (column: {index: number}) => {
        if (column.index === 0) {
            return TABLE_COLUMN_USER_WIDTH;
        }

        return this.props.data && this.props.data.compareTo
            ? TABLE_COLUMN_WIDTH_COMPARISON
            : TABLE_COLUMN_WIDTH_NO_COMPARISON;
    };

    cellRenderer = (cell: {key: string; style: any; rowIndex: number; columnIndex: number}) => {
        const {data, totals} = this.props;
        if (!data || !totals) return undefined;

        const {key, style, rowIndex, columnIndex} = cell;

        if (rowIndex === 0) {
            return this.createHeaderCell(columnIndex, totals, key, style);
        } else if (columnIndex === 0) {
            return rowIndex === 1 ? (
                <div key={key} style={style} className={styles.title}>
                    Totals
                </div>
            ) : (
                <ActivityTableNameCell
                    key={key}
                    style={style}
                    rowIndex={rowIndex - 1}
                    data={data}
                />
            );
        } else if (rowIndex === 1) {
            return (
                <ActivityTableQuotaCell
                    key={key}
                    // @ts-expect-error
                    style={{...style, backgroundColor: styles['header-bg-color']}}
                    isTotalRow={true}
                    columnIndex={columnIndex}
                    data={data}
                    totals={totals}
                />
            );
        } else {
            return (
                <ActivityTableQuotaCell
                    key={key}
                    style={style}
                    rowIndex={rowIndex - 1}
                    columnIndex={columnIndex}
                    data={data}
                />
            );
        }
    };

    createHeaderCell = (
        columnIndex: number,
        totals: ComparedActivityReportSummary<ReportRowData>,
        key?: string,
        style?: any
    ) => {
        const columnTitles = getColumnHeaders(totals.currentPeriod);
        const column = columnTitles[columnIndex];
        const columnTitle = column && column.title;
        const columnIcon = column && column.iconType;
        const columnSorter = () => {
            // @ts-expect-error
            this.props.onColumnSort(columnTitle);
        };
        let sortDirection = null;
        if (this.props.activeSort && this.props.activeSort.by === columnTitle) {
            sortDirection = this.props.activeSort.direction;
        }

        return (
            <ActivityTableHeaderCell
                key={key}
                style={style}
                // @ts-expect-error
                title={columnTitle}
                iconType={columnIcon}
                onClick={columnSorter}
                sortDirection={sortDirection}
                totals={totals}
            />
        );
    };
}

export const getColumnHeaders = (
    currentTotals: ReportRowData
): Array<{
    title: string;
    iconType: number | null | undefined;
}> => {
    const userHeader = {title: USER_SORT_STRING, iconType: undefined} as const;
    if (currentTotals.segments) {
        const titles = [userHeader].concat(
            // @ts-expect-error
            currentTotals.segments.map((segment) => {
                return {title: segment.title, iconType: segment.iconType};
            })
        );

        return titles;
    }

    return [userHeader];
};
